import { isScoredInteractionMode } from "@/lib/scenarios/interactionModes";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import * as didSdk from "@d-id/client-sdk";
import { Button } from "@/components/ui/button";
import { did, waitForDidAgentReady } from "@/lib/scenarios/providers/did/client";
import {
  didSessionAdapter,
  forgetDidSession,
  registerDidSession,
} from "@/lib/scenarios/providers/did/sessionAdapter";
import {
  DID_PRESENT_DOCUMENT_TOOL,
  DID_CLOSE_DOCUMENT_TOOL,
  DID_SHOW_PAGE_TOOL,
  DID_READ_PAGE_TOOL,
} from "@/lib/scenarios/providers/did/presentationTool";
import {
  readPageResult,
  showPageResult,
  waitForPageableControl,
  withToolAnswer,
} from "@/lib/scenarios/providers/presentationTools";
import {
  END_FLOW_TIMEOUT_MS,
  SCORE_FLOW_TIMEOUT_MS,
  withTimeout,
} from "@/lib/scenarios/providers/sessionTimeouts";

import {
  startTranscript,
  appendTranscriptTurn,
  clearTranscript,
} from "@/lib/scenarios/providers/did/transcriptStore";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { postToAncestors } from "@/components/session/postToAncestors";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import { createLogger } from "@/lib/frontend-logger";
import { PreSessionScreen } from "@/components/session/PreSessionScreen";
import { SessionStartOverlay } from "@/components/session/SessionStartOverlay";
import { PostSessionScreen } from "@/components/session/PostSessionScreen";
import { WaitingForScoreScreen } from "@/components/session/WaitingForScoreScreen";
import { ScoreScreen } from "@/components/session/ScoreScreen";
import { AvatarVideoArea } from "@/components/session/AvatarVideoArea";
import { SessionControls } from "@/components/session/SessionControls";
import type { DocumentControl } from "@/components/session/DisplayDocument";
import { PresentedDocument } from "@/components/session/PresentedDocument";
import { SessionStage } from "@/components/session/SessionStage";
import { withCacheBust } from "@/lib/utils";

const logger = createLogger("did.frame");
type PageState = "idle" | "connecting" | "connected" | "awaiting-score" | "scored" | "ended";

type DidManager = Awaited<ReturnType<typeof didSdk.createAgentManager>>;

/** Client-tool arguments arrive as a JSON string; a bad payload is not fatal. */
function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * D-ID session runtime. Uses `@d-id/client-sdk` (`createAgentManager`) to
 * open a WebRTC/LiveKit stream against the agent we provisioned at save
 * time, and delegates all presentation to the shared session components so
 * the learner experience matches HeyGen and Tavus.
 */
export function DidFrame({
  scenario,
  publicLinkToken,
}: {
  scenario: Scenario;
  publicLinkToken?: string;
}) {
  const content = scenario.content ?? {};
  const didBlock = content.did;
  const auto_start = content.auto_start ?? false;
  const redirectUrl = content.redirect_url?.trim();
  const interactionMode = didBlock?.interaction_mode;
  const isScoredMode = isScoredInteractionMode(interactionMode);
  const display = content.display;
  const hasDisplay = !!display?.source;
  /**
   * When the trainer let the avatar present, the document stays hidden until
   * the agent calls the `present_document` client tool.
   */
  const avatarPresents =
    interactionMode === "conversational" && !!didBlock?.present_document && hasDisplay;
  const displayLayout = display?.layout ?? "side-by-side";
  const avatarPosition: "left" | "right" = display?.avatar_position ?? "right";

  const [pageState, setPageState] = useState<PageState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [presentOpen, setPresentOpen] = useState(false);
  // Imperative handle on the document viewer, driven by the avatar's paging
  // tools. Only populated while a PDF is on screen.
  const documentControlRef = useRef<DocumentControl | null>(null);
  const [score, setScore] = useState<ScoreResult | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);




  const managerRef = useRef<DidManager | null>(null);
  const startedRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  /** D-ID's own id for this conversation (`cht_...`), from `onNewChat`. */
  const chatIdRef = useRef<string | null>(null);

  const micStreamRef = useRef<MediaStream | null>(null);
  const micPublishedRef = useRef(false);
  const micPublishInFlightRef = useRef(false);
  const greetedRef = useRef(false);
  const trackedRef = useRef(false);
  const pageStateRef = useRef<PageState>("idle");
  /** Message keys already written to the transcript buffer. */
  const seenMessagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    pageStateRef.current = pageState;
  }, [pageState]);

  const tracking = useSessionTracking({
    scenario,
    publicLinkToken,
    adapter: didSessionAdapter,
  });

  const ready = Boolean(didBlock?.agent_id);

  /**
   * Ensure a transcript buffer exists for the current session.
   *
   * The D-ID session id arrives with `onStreamCreated`, which normally lands
   * before the first message. If a message somehow beats it, fall back to a
   * locally minted id so no turn is lost.
   */
  const ensureTranscriptSession = useCallback(() => {
    let sid = sessionIdRef.current;
    if (!sid) {
      sid = `did-${Date.now()}`;
      sessionIdRef.current = sid;
    }
    return sid;
  }, []);

  /**
   * D-ID exposes no endpoint that reads a chat back, so the transcript is
   * captured here as the conversation happens. Assistant turns are only
   * recorded on `answer` (the completed reply) — `partial` events repeat the
   * same message while it streams.
   */
  const captureMessages = useCallback(
    (messages: unknown, type: unknown) => {
      const list = Array.isArray(messages) ? messages : [];
      const isAnswer = String(type) === "answer";
      const sid = ensureTranscriptSession();
      for (const raw of list) {
        const m = raw as {
          id?: string;
          role?: string;
          content?: string;
          created_at?: string;
        };
        const text = (m?.content ?? "").trim();
        if (!text) continue;
        const role = m?.role === "user" ? "user" : "avatar";
        // Assistant text is only final once the answer event arrives.
        if (role === "avatar" && !isAnswer) continue;
        const key = `${role}:${m?.id ?? m?.created_at ?? text}`;
        if (seenMessagesRef.current.has(key)) continue;
        seenMessagesRef.current.add(key);
        appendTranscriptTurn(sid, role, text);
      }
    },
    [ensureTranscriptSession],
  );

  const stopStream = (stream: MediaStream | null) => {
    if (!stream) return;
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
  };

  const cleanup = useCallback(async () => {
    const mgr = managerRef.current;
    managerRef.current = null;
    if (mgr) {
      try {
        await mgr.disconnect();
      } catch (e) {
        logger.warn("did disconnect failed", { err: (e as Error)?.message });
      }
    }
    stopStream(micStreamRef.current);
    micStreamRef.current = null;
    micPublishedRef.current = false;
    micPublishInFlightRef.current = false;
    setRemoteStream(null);
  }, []);

  /**
   * Publish the learner's microphone to the live session.
   *
   * `AgentManagerOptions.microphoneStream` is declared in the SDK typings but
   * is never read by the shipped bundle (v1.2.10), so the mic must be
   * published explicitly through `publishMicrophoneStream` once connected.
   * Only LiveKit (Expressive / V4) agents expose the method — clip agents
   * reject it, which is why failures are surfaced instead of swallowed.
   */
  const publishMic = useCallback(
    async (manager: DidManager) => {
      if (micPublishedRef.current || micPublishInFlightRef.current) return;
      micPublishInFlightRef.current = true;
      try {
        const publish = manager.publishMicrophoneStream;
        if (typeof publish !== "function") {
          setMediaError(
            "This avatar can't receive microphone audio. Re-save the scenario to provision a voice-capable agent.",
          );
          logger.error("did mic publish unavailable", {
            scenario_id: scenario.id,
            stream_type: String(manager.getStreamType?.() ?? "unknown"),
          });
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;
        await publish.call(manager, stream);
        micPublishedRef.current = true;
        setMediaError(null);
        logger.info("did mic published", {
          scenario_id: scenario.id,
          stream_type: String(manager.getStreamType?.() ?? "unknown"),
        });
      } catch (e) {
        stopStream(micStreamRef.current);
        micStreamRef.current = null;
        micPublishedRef.current = false;
        const msg = (e as Error)?.message ?? String(e);
        setMediaError("We couldn't turn on your microphone, so the avatar can't hear you.");
        logger.error("did mic publish failed", { scenario_id: scenario.id, err: msg });
      } finally {
        micPublishInFlightRef.current = false;
      }
    },
    [scenario.id],
  );

  // The D-ID SDK never plays `agent.greetings` on its own — `connect()` only
  // opens the chat + stream and then waits for input. Speak the opening line
  // ourselves, verbatim, exactly once per session.
  const speakGreeting = useCallback(
    (manager: DidManager) => {
      if (greetedRef.current) return;
      const fromAgent = (manager.agent as { greetings?: string[] } | undefined)?.greetings?.[0];
      const greeting = (fromAgent || didBlock?.opening_text || "").trim();
      if (!greeting) return;
      greetedRef.current = true;
      void Promise.resolve(manager.speak({ type: "text", input: greeting })).catch((e) => {
        greetedRef.current = false;
        logger.warn("did greeting speak failed", {
          err: (e as Error)?.message,
        });
      });
    },
    [didBlock?.opening_text],
  );

  const failSession = useCallback(
    (message: string, meta?: Record<string, unknown>) => {
      logger.error("did session failed", {
        scenario_id: scenario.id,
        error: message,
        ...(meta ?? {}),
      });
      setError(message);
      setPageState("ended");
      startedRef.current = false;
      void cleanup();
    },
    [cleanup, scenario.id],
  );

  const startSession = useCallback(async () => {
    if (!ready || !didBlock?.agent_id) return;
    if (startedRef.current) return;
    startedRef.current = true;
    setError(null);
    setMediaError(null);
    setIsEnding(false);
    setVideoReady(false);
    setPresentOpen(false);
    setPageState("connecting");
    greetedRef.current = false;
    trackedRef.current = false;
    seenMessagesRef.current = new Set();
    try {
      // Presenter/background edits are asynchronous in D-ID. Never request a
      // client key or connect while the regenerated agent is unavailable.
      await waitForDidAgentReady(didBlock.agent_id);
      const { client_key } = await did.createClientKey(didBlock.agent_id);
      // Functional mode keeps the chat side voice-capable; the agent itself
      // must also be Expressive (V4 / LiveKit) for microphone publishing.
      const ChatModeEnum = (didSdk as unknown as { ChatMode?: Record<string, unknown> }).ChatMode;
      const voiceMode =
        (ChatModeEnum?.Functional as unknown) ??
        (ChatModeEnum?.Voice as unknown) ??
        (ChatModeEnum?.functional as unknown);
      const greetingText = (didBlock?.opening_text || "").trim();
      const managerOptions: Record<string, unknown> = {
        auth: { type: "key", clientKey: client_key },
        // Warm the stream before the first utterance so the greeting is not
        // clipped while the media pipeline is still coming up.
        streamOptions: { streamWarmup: true, compatibilityMode: "auto" },
        callbacks: {
          onSrcObjectReady: (srcObject: MediaStream) => {
            // `AvatarVideoArea` owns attachment, autoplay recovery and the
            // "tap for sound" affordance — just hand it the stream.
            setRemoteStream((prev) =>
              prev && srcObject && prev.id === srcObject.id ? prev : srcObject,
            );
          },
          onConnectionStateChange: (state: unknown) => {
            const s = String(state).toLowerCase();
            if (s === "connected") {
              const mgr = managerRef.current;
              logger.info("did connected", {
                scenario_id: scenario.id,
                stream_type: String(mgr?.getStreamType?.() ?? "unknown"),
                interrupt_available: mgr?.getIsInterruptAvailable?.() ?? null,
              });
              setPageState("connected");
              const sid0 = ensureTranscriptSession();
              startTranscript(sid0);
              tracking.markStarted();
              // The greeting bypasses the LLM, so record it explicitly.
              const greetingLine = (
                (mgr?.agent as { greetings?: string[] } | undefined)?.greetings?.[0] ||
                didBlock?.opening_text ||
                ""
              ).trim();
              if (greetingLine) {
                seenMessagesRef.current.add(`avatar:${greetingLine}`);
                appendTranscriptTurn(sid0, "avatar", greetingLine);
              }
              if (mgr) {
                speakGreeting(mgr);
                void publishMic(mgr);
              }
              const sid = sessionIdRef.current ?? sid0;
              postToAncestors({
                event: "sessionStarted",
                sessionId: sid,
                scenarioId: scenario.id,
              });
              trackEvent({
                category: ANALYTICS_CATEGORIES.SESSION,
                action: ANALYTICS_ACTIONS.STARTED,
                ga4EventName: "session_started",
                extraData: {
                  scenario_id: scenario.id,
                  session_id: sid,
                  provider: "did",
                  mode: interactionMode ?? "conversational",
                },
              });
            } else if (s === "fail" || s === "disconnected") {
              logger.warn("did disconnected", { state: s });
              setPageState((prev) => (prev === "connecting" ? "ended" : prev));
            }
          },
          onStreamCreated: (info: unknown) => {
            const sid = (info as { session_id?: string })?.session_id;
            // Never re-key a session that already has a transcript buffer (and
            // possibly a registered chat id) under a different id.
            if (sid && !sessionIdRef.current) sessionIdRef.current = sid;
          },

          /**
           * D-ID keys its own session records by chat id, not by the stream
           * session id we track with. Pair them so the session adapter can
           * read the vendor's metadata and, if needed, its chat log.
           */
          onNewChat: (chatId: unknown) => {
            const cid = String(chatId ?? "").trim();
            if (!cid) return;
            chatIdRef.current = cid;
            registerDidSession({
              sessionId: ensureTranscriptSession(),
              chatId: cid,
              agentId: didBlock?.agent_id,
            });
            logger.info("did chat id registered", { chat_id: cid });
          },

          onNewMessage: (messages: unknown, type: unknown) => {
            captureMessages(messages, type);
          },
          onVideoStateChange: (state: unknown) => {
            const s = String(state).toLowerCase();
            if (s.includes("ready") || s === "start") {
              if (managerRef.current) speakGreeting(managerRef.current);
            }
          },
          onError: (err: Error, data: unknown) => {
            failSession(err.message || "D-ID stream error", { data });
          },
          onToolEvent: (event: unknown) => {
            const e = event as { type?: string; payload?: { name?: string } };
            logger.info("did tool event", {
              scenario_id: scenario.id,
              type: e?.type ?? null,
              tool: e?.payload?.name ?? null,
            });
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      };
      if (voiceMode !== undefined) managerOptions.mode = voiceMode;
      if (greetingText) {
        // Keep the transcript / LLM context aware of the spoken greeting even
        // though it bypassed the LLM.
        managerOptions.initialMessages = [
          {
            id: `greeting-${Date.now()}`,
            role: "assistant",
            content: greetingText,
            parts: [{ type: "text", text: greetingText }],
            created_at: new Date().toISOString(),
          },
        ];
      }
      const manager = await didSdk.createAgentManager(
        didBlock.agent_id,
        managerOptions as unknown as Parameters<typeof didSdk.createAgentManager>[1],
      );
      managerRef.current = manager;
      // Client tools run in the browser: the agent calls `present_document`
      // / `close_document` and these handlers flip our own split-screen
      // layout. Handlers may be registered before `connect()` — the SDK
      // buffers them.
      const registerClientTool = (
        manager as unknown as {
          registerClientTool?: (
            name: string,
            handler: (args: unknown) => Promise<string>,
          ) => void;
        }
      ).registerClientTool;
      if (avatarPresents) {
        if (typeof registerClientTool === "function") {
          // Every handler answers within a bounded time: a call the model never
          // sees answered leaves the avatar mute for the rest of the turn.
          const logOutcome = (info: {
            tool: string;
            outcome: string;
            detail?: string;
          }) => logger.info("did document tool outcome", { ...info, scenario_id: scenario.id });

          registerClientTool.call(manager, DID_PRESENT_DOCUMENT_TOOL, async () =>
            JSON.stringify(
              await withToolAnswer(
                DID_PRESENT_DOCUMENT_TOOL,
                () => {
                  setPresentOpen(true);
                  return { shown: true };
                },
                logOutcome,
              ),
            ),
          );
          registerClientTool.call(manager, DID_CLOSE_DOCUMENT_TOOL, async () =>
            JSON.stringify(
              await withToolAnswer(
                DID_CLOSE_DOCUMENT_TOOL,
                () => {
                  setPresentOpen(false);
                  return { shown: false };
                },
                logOutcome,
              ),
            ),
          );
          // Paging tools: only attached upstream for PDF display sources, but
          // registered unconditionally so a stale attachment answers honestly
          // instead of leaving the agent waiting on an unhandled call.
          registerClientTool.call(manager, DID_SHOW_PAGE_TOOL, async (args) => {
            const raw = typeof args === "string" ? safeJson(args) : args;
            const page = Number((raw as { page?: unknown } | null)?.page);
            setPresentOpen(true);
            return JSON.stringify(
              await withToolAnswer(
                DID_SHOW_PAGE_TOOL,
                async () => {
                  // The viewer may only just be mounting (document was hidden) —
                  // wait for it to be pageable rather than assuming one frame.
                  const control = await waitForPageableControl(
                    () => documentControlRef.current,
                  );
                  return showPageResult(control, page);
                },
                logOutcome,
              ),
            );
          });
          registerClientTool.call(manager, DID_READ_PAGE_TOOL, async () =>
            JSON.stringify(
              await withToolAnswer(
                DID_READ_PAGE_TOOL,
                async () => {
                  const control = await waitForPageableControl(
                    () => documentControlRef.current,
                  );
                  return readPageResult(control);
                },
                logOutcome,
              ),
            ),
          );
        } else {
          logger.warn("did sdk has no registerClientTool — presentation unavailable", {
            scenario_id: scenario.id,
          });
        }
      }
      await manager.connect();
    } catch (e) {
      failSession((e as Error).message || "Failed to start D-ID session");
    }
  }, [
    ready,
    didBlock?.agent_id,
    didBlock?.opening_text,
    scenario.id,
    failSession,
    speakGreeting,
    publishMic,
    captureMessages,
    avatarPresents,
    ensureTranscriptSession,
    interactionMode,
    tracking,
  ]);

  const handleToggleMute = useCallback(async () => {
    const next = !isMuted;
    const mgr = managerRef.current;
    // Unmuting before the mic was ever published (e.g. permission denied on
    // connect) retries the publish instead of toggling a track that isn't there.
    if (!next && !micPublishedRef.current && mgr) {
      await publishMic(mgr);
      if (!micPublishedRef.current) return;
    }
    const stream = micStreamRef.current;
    if (stream) {
      stream.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
    }
    setIsMuted(next);
  }, [isMuted, publishMic]);

  const handleEnd = useCallback(async () => {
    const sid = sessionIdRef.current;
    setIsEnding(true);
    tracking.markEnded();
    trackEvent({
      category: ANALYTICS_CATEGORIES.SESSION,
      action: ANALYTICS_ACTIONS.COMPLETED,
      ga4EventName: "session_completed",
      extraData: { scenario_id: scenario.id, session_id: sid, provider: "did" },
    });
    if (isScoredMode) {
      setScore(null);
      setScoreError(null);
      pageStateRef.current = "awaiting-score";
      setPageState("awaiting-score");
    }

    // Tear down media first — the transcript buffer is local, so unlike the
    // polled providers nothing else is still in flight.
    try {
      await withTimeout(cleanup(), END_FLOW_TIMEOUT_MS, "Timed out while ending D-ID session");
    } catch (e) {
      logger.warn("did cleanup timed out or failed", {
        scenario_id: scenario.id,
        session_id: sid,
        error: (e as Error)?.message ?? String(e),
      });
    }

    let transcriptObjectId: string | null = null;
    if (sid && !trackedRef.current) {
      trackedRef.current = true;
      try {
        const res = await withTimeout(
          tracking.trackOnEnd({ sessionId: sid }),
          END_FLOW_TIMEOUT_MS,
          "Timed out while saving D-ID session transcript",
        );
        transcriptObjectId = res.transcriptObjectId;
      } catch (e) {
        const message =
          "We couldn't save your transcript in time, so your score can't be calculated right now. Please try again later or contact your trainer.";
        logger.error("did tracking failed", {
          scenario_id: scenario.id,
          session_id: sid,
          error: (e as Error)?.message ?? String(e),
        });
        if (isScoredMode) {
          setScoreError(message);
          pageStateRef.current = "scored";
          setPageState("scored");
        } else {
          setError(message);
          pageStateRef.current = "ended";
          setPageState("ended");
        }
        postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
        return;
      }
    }
    if (sid) clearTranscript(sid);

    if (redirectUrl) {
      postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
      window.location.href = redirectUrl;
      return;
    }

    if (isScoredMode) {
      if (transcriptObjectId) {
        let scoring: Awaited<ReturnType<typeof tracking.runScoringJob>>;
        try {
          scoring = await withTimeout(
            tracking.runScoringJob(transcriptObjectId),
            SCORE_FLOW_TIMEOUT_MS,
            "Timed out while waiting for D-ID score",
          );
        } catch (e) {
          logger.error("did scoring timed out or failed", {
            scenario_id: scenario.id,
            session_id: sid,
            transcript_object_id: transcriptObjectId,
            error: (e as Error)?.message ?? String(e),
          });
          scoring = {
            result: null,
            error:
              "We couldn't score your session right now. Please try again later or contact your trainer if the problem persists.",
            aborted: false,
          };
        }
        const { result, error: sErr, aborted } = scoring;
        if (aborted) return;
        if (result) {
          setScore(result);
          postToAncestors({
            event: "sessionScored",
            sessionId: sid,
            scenarioId: scenario.id,
            score: result,
          });
        } else if (sErr) {
          setScoreError(sErr);
        }
      } else {
        setScoreError(
          "We saved your session but couldn't find the transcript object needed for scoring. Please try again later or contact your trainer.",
        );
      }
      pageStateRef.current = "scored";
      setPageState("scored");
      postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
      return;
    }

    postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
    pageStateRef.current = "ended";
    setPageState("ended");
  }, [cleanup, isScoredMode, redirectUrl, scenario.id, tracking]);

  const handleRestart = useCallback(async () => {
    await cleanup();
    startedRef.current = false;
    greetedRef.current = false;
    trackedRef.current = false;
    if (sessionIdRef.current) forgetDidSession(sessionIdRef.current);
    sessionIdRef.current = null;
    chatIdRef.current = null;

    seenMessagesRef.current = new Set();
    setScore(null);
    setScoreError(null);
    setError(null);
    setMediaError(null);
    setIsMuted(false);
    setIsEnding(false);
    setVideoReady(false);
    setPresentOpen(false);
    tracking.reset();
    pageStateRef.current = "idle";
    setPageState("idle");
  }, [cleanup, tracking]);

  const handleBack = useCallback(() => {
    postToAncestors({ event: "sessionBack", scenarioId: scenario.id });
    if (typeof window !== "undefined" && window.parent === window) {
      try {
        window.history.back();
      } catch {
        /* ignore */
      }
    }
  }, [scenario.id]);

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  // Best-effort persistence when the tab closes mid-session.
  useEffect(() => {
    const handler = () => {
      const st = pageStateRef.current;
      const live = st === "connecting" || st === "connected";
      if (!live || trackedRef.current) return;
      trackedRef.current = true;
      tracking.beaconOnUnload(sessionIdRef.current);
    };
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, [tracking]);

  if (!ready) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-background text-center px-6">
        <p className="text-sm text-muted-foreground">
          This D-ID scenario is missing an agent. Open the editor and save to provision it.
        </p>
      </div>
    );
  }

  if (pageState === "awaiting-score") return <WaitingForScoreScreen scenario={scenario} />;

  if (pageState === "scored") {
    return (
      <ScoreScreen
        scenario={scenario}
        score={score}
        error={scoreError}
        onRestart={() => void handleRestart()}
      />
    );
  }

  if (pageState === "ended") {
    return (
      <PostSessionScreen
        scenario={scenario}
        subtitle={error ?? undefined}
        onRestart={() => void handleRestart()}
      />
    );
  }

  // Teardown / transcript / scoring window: show the post-session screen with
  // restart disabled instead of falling through to the start screen.
  if (isEnding) {
    return <PostSessionScreen scenario={scenario} onRestart={() => void handleRestart()} busy />;
  }

  const isConnecting = pageState === "connecting";
  const hasVideo =
    !!remoteStream && remoteStream.getVideoTracks().some((t) => t.readyState === "live");
  const isLive = pageState === "connected" && hasVideo && !isEnding;

  const commonControlsProps = {
    isMuted,
    onToggleMute: () => void handleToggleMute(),
    onEnd: () => void handleEnd(),
  };

  const avatarNode = (
    <AvatarVideoArea
      previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
      mediaStream={remoteStream}
      errorMessage={mediaError}
      {...commonControlsProps}
      hideControls
      hidePoster
      onNativeRatio={() => setVideoReady(true)}
    />
  );

  const showDocument = hasDisplay && (!avatarPresents || presentOpen);
  const documentNode =
    showDocument && display?.source ? (
      <PresentedDocument
        scenarioId={scenario.id}
        source={display.source}
        publicLinkToken={publicLinkToken}
        controlRef={documentControlRef}
        onClose={avatarPresents ? () => setPresentOpen(false) : undefined}
      />
    ) : null;

  return (
    <div className="relative flex h-[100dvh] w-screen bg-background">
      {!isConnecting && !isEnding && !isLive && (
        <Button
          onClick={handleBack}
          size="icon"
          variant="secondary"
          className="absolute top-4 left-4 z-20 rounded-lg bg-card/70 backdrop-blur"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}
      {isLive && (
        <div className="relative flex-1 min-h-0 flex flex-col">
          <SessionStage
            documentNode={documentNode}
            layout={displayLayout}
            avatarPosition={avatarPosition}
            renderAvatar={() => avatarNode}
          />
          {videoReady && (
            <SessionControls
              {...commonControlsProps}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30"
            />
          )}
        </div>
      )}
      {(!isLive || !videoReady) &&
        (auto_start ? (
          <div className="absolute inset-0 z-10">
            <SessionStartOverlay
              previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
              busy={isConnecting || (isLive && !videoReady)}
              onStart={() => void startSession()}
              errorMessage={error}
            />
          </div>
        ) : (
          <div className="absolute inset-0 z-10">
            <PreSessionScreen
              scenario={scenario}
              connecting={isConnecting || (isLive && !videoReady)}
              onStart={() => void startSession()}
              onBack={handleBack}
            />
          </div>
        ))}
    </div>
  );
}
